import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
  rounded?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className = '', 
  width = 'w-full', 
  height = 'h-4', 
  rounded = false 
}) => {
  const roundedClass = rounded ? 'rounded-full' : 'rounded-md';
  
  return (
    <div 
      className={`${width} ${height} ${roundedClass} bg-gray-200 animate-pulse ${className}`}
    />
  );
};

// Dashboard-specific skeleton components
export const DashboardStatSkeleton: React.FC = () => (
  <div className="bg-white p-6 rounded-lg shadow-sm border">
    <div className="flex items-center justify-between mb-2">
      <Skeleton width="w-24" height="h-4" />
      <Skeleton width="w-6" height="h-6" rounded />
    </div>
    <Skeleton width="w-16" height="h-8" className="mb-1" />
    <Skeleton width="w-32" height="h-3" />
  </div>
);

export const DashboardChartSkeleton: React.FC = () => (
  <div className="bg-white p-6 rounded-lg shadow-sm border">
    <div className="mb-6">
      <Skeleton width="w-40" height="h-6" className="mb-2" />
      <Skeleton width="w-64" height="h-4" />
    </div>
    <div className="h-64 flex items-center justify-center">
      <div className="w-32 h-32 rounded-full border-4 border-gray-200 border-t-gray-300 animate-spin" />
    </div>
  </div>
);

export const RecentActivitySkeleton: React.FC = () => (
  <div className="bg-white p-6 rounded-lg shadow-sm border">
    <div className="mb-6">
      <Skeleton width="w-32" height="h-6" className="mb-2" />
      <Skeleton width="w-48" height="h-4" />
    </div>
    <div className="space-y-4">
      {[...Array(5)].map((_, index) => (
        <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
          <Skeleton width="w-8" height="h-8" rounded />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton width="w-24" height="h-4" />
              <Skeleton width="w-16" height="h-3" />
            </div>
            <Skeleton width="w-full" height="h-3" />
            <Skeleton width="w-3/4" height="h-3" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const DashboardSkeleton: React.FC = () => (
  <div className="p-6 space-y-6">
    {/* Header skeleton */}
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center space-x-4">
        <Skeleton width="w-12" height="h-12" rounded />
        <div>
          <Skeleton width="w-48" height="h-6" className="mb-2" />
          <Skeleton width="w-32" height="h-4" />
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <Skeleton width="w-32" height="h-10" />
        <Skeleton width="w-24" height="h-10" />
      </div>
    </div>

    {/* Stats grid skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {[...Array(4)].map((_, index) => (
        <DashboardStatSkeleton key={index} />
      ))}
    </div>

    {/* Charts grid skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      {[...Array(3)].map((_, index) => (
        <DashboardChartSkeleton key={index} />
      ))}
    </div>

    {/* Recent activity skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <RecentActivitySkeleton />
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="mb-6">
          <Skeleton width="w-40" height="h-6" className="mb-2" />
          <Skeleton width="w-56" height="h-4" />
        </div>
        <div className="h-64">
          <Skeleton width="w-full" height="h-full" />
        </div>
      </div>
    </div>
  </div>
); 